/*
 * <oilogic-add-to-cart> — AJAX add-to-cart for product cards.
 * Loaded by several sections at once (cart drawer + any card grid on the same
 * page), so the browser executes this file more than once. The IIFE keeps the
 * class binding off the top level (a bare top-level `class` throws
 * "redeclaration of let" on the second evaluation) and the guard makes the
 * re-run a no-op.
 */
(() => {
  class OilogicAddToCart extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.form = this.querySelector('form');
      this.button = this.querySelector('[type="submit"]');
      this.label = this.querySelector('[data-atc-label]');
      this.resetTimer = null;

      if (this.form) {
        this.form.addEventListener('submit', (event) => this.onSubmit(event), { signal: this.controller.signal });
      }
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      clearTimeout(this.resetTimer);
      clearTimeout(this.addedTimer);
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.button.disabled) return;
      this.button.disabled = true;
      this.button.dataset.loading = '';
      delete this.button.dataset.added;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: new FormData(this.form),
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error('add-to-cart failed');

        const cart = await this.updateCount();
        this.flash();
        document.dispatchEvent(new CustomEvent('oilogic:cart:added', { bubbles: true, detail: { cart } }));
        delete this.button.dataset.loading;
        this.button.disabled = false;
        // Styling hook for consumers that animate the content back in.
        this.button.dataset.added = '';
        clearTimeout(this.addedTimer);
        this.addedTimer = setTimeout(() => delete this.button.dataset.added, 1500);
      } catch (error) {
        // Fall back to a native form submission (full-page add to cart).
        this.form.submit();
      }
    }

    async updateCount() {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return null;
      const cart = await response.json();
      document.querySelectorAll('[data-cart-count]').forEach((element) => {
        element.textContent = cart.item_count;
        element.hidden = cart.item_count === 0;
      });
      return cart;
    }

    flash() {
      if (!this.label) return;
      const original = this.label.dataset.original || this.label.textContent;
      this.label.dataset.original = original;
      this.label.textContent = this.dataset.addedLabel || original;
      clearTimeout(this.resetTimer);
      this.resetTimer = setTimeout(() => {
        this.label.textContent = original;
      }, 1500);
    }
  }

  if (!customElements.get('oilogic-add-to-cart')) {
    customElements.define('oilogic-add-to-cart', OilogicAddToCart);
  }
})();
